/***
 * Created on 2023/10/3
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useState} from 'react';
import {useOnChange, useLanguage} from 'common/Hook';
import {getErrorMessage, URLHandler, getCurrentDomainRegion, getQNAPUrl, convertToQNAPLang, getPrivacyPolicyUrl, CHINA} from 'common/ServiceCommon';
import md5 from 'blueimp-md5';
import Captcha from "./common/captcha";
import {useQueryClient} from "@tanstack/react-query";
import {apiPost, executeGrecaptcha} from "../service/api";
import SocialAccount from "./common/social/social_account";
import {useTranslation} from "react-i18next";
import sys_info_svg from '../static/img/utils/ic-sys-info.svg';
import {Tooltip} from "react-tooltip";


function SigninComponent({}) {
    const { t, i18n} = useTranslation();
    useLanguage();
    const urlHandler = new URLHandler(location.href);
    const queryClient = useQueryClient()
    const [clientId, setClientId] = useState(null);
    const [app, setApp] = useState(null);
    const [signInOption, setSignInOption] =useState('qid');
    const [visiblePasswd, setVisiblePasswd] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const [disableSignInBtn, setDisableSignInBtn] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [reachLoginLimit, setReachLoginLimit] = useState(false);
    const {onChange, values, setValues} = useOnChange({
        qid: urlHandler.qid || '',
        password: '',
        captcha: '',
        keep_me_signed_in: false
    });
    const getClientId = (url) => {
        const parsed_url = new URL(url);
        const state = parsed_url.searchParams.get('state');
        if (state) {
            const sub_params = state.split('?')?.[1];
            if (sub_params) {
                const parsed_sub_params = new URLSearchParams(sub_params);
                setClientId(parsed_sub_params.get('client_id'));
            }
        }
        return null;
    }

     const getApp = async () => {
        let session_apps = sessionStorage.getItem('apps');
        let _app;
        if (session_apps) {
            session_apps = JSON.parse(session_apps);
            _app = session_apps?.[appId];
        }
        if (!_app || Object.keys(_app).length <= 0) {
            const resp = await fetch(`/ajax/app/${clientId}`);
            const res = await resp.json();
            if (res && Object.keys(res).length > 0) {
                if (!session_apps) {
                    session_apps = {}
                }
                session_apps[clientId] = res;
                sessionStorage.setItem('apps', JSON.stringify(session_apps));
            }
            setApp(res);
        }
        else {
            setApp(_app);
        }
    }

    const togglePassword = () => {
        setVisiblePasswd(prev => !prev);
    }

    const toggleSignOption = (e) => {
        e.preventDefault();
        signInOption === 'qid' ? setSignInOption('social') : setSignInOption('qid');
    }

    const onLogin = async (e) => {
        e.preventDefault();
        setErrorMessage(null);
        setDisableSignInBtn(true);
        await executeGrecaptcha("SIGNIN", async (recaptcha_token) => {
            const data = {
                recaptcha_token,
                qid: values.qid,
                client_id: clientId,
                password: md5(values.password),
                keep_me_signed_in: values.keep_me_signed_in
            }
            if (values?.captcha) {
                data.captcha = values.captcha;
            }
            try {
                const res = await apiPost('/signin', data)
                console.log(res);
                if (res?.redirect_url === 'two-step-verification') {
                    const query = new URL(location.href).search;
                    location.href = `/${res.redirect_url}${query}`;
                    return;
                }
                else if (res?.auth_state === 'authorized') {
                    location.href = `/login${location.search}`;
                    return;
                }
                else {
                    location.reload();
                }
            } catch (error) {
                console.log(error);
                setDisableSignInBtn(false);
                // don't show error message for recaptcha
                if (!['1019014', '1019016'].includes(error?.cause?.code.toString())) {
                    setErrorMessage(getErrorMessage(t, error.cause?.code));
                    if (error.cause?.code === 1019007) {
                        setReachLoginLimit(true);
                    }
                }
            }
            await queryClient.invalidateQueries(['get_captcha_image'])
        }, async (error) => {
            console.log(error);
            setDisableSignInBtn(false);
            setErrorMessage(getErrorMessage(t, 1019017));
            console.log('Possibly that reCaptcha token is expired.');
            await queryClient.invalidateQueries(['get_captcha_image'])
        });

    }

    const onSSOLogin = async (e) => {
        e.preventDefault();
        const sso_id = urlHandler.getSAMLSSOId()
        if (!sso_id) {
            location.href = `/saml/sso${location.search}`;
            return;
        }
        else {
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('email');
            location.href = `/saml/sso?${searchParams.toString()}`;
            return;
        }
    }

    useEffect(() => {
        sessionStorage.clear();
        localStorage.removeItem('currentUser');
        getClientId(window.location.href);
        if (clientId) {
            getApp();
        }
    }, [clientId]);

    return (
        <section className="common-page-wrapper common-page-wrapper--sign-in" data-location="cn">
            <div className="common-page-header">
                <p>{t('@Sign in to QNAP Account@')}</p>
                {
                    app &&
                    <p className="continue-to">{t('@to continue to {app_name}@', {
                        app_name: app.display_name,
                        interpolation: {
                            prefix: '{',
                            suffix: '}',
                            escapeValue: false
                        }
                    })}</p>
                }
            </div>
            <form onSubmit={async (e) => await onLogin(e)}>
            <div className="common-page-body">
                {signInOption === 'social' && <div className="sign-in--sns"><SocialAccount /></div>}
                {signInOption === 'qid' &&
                    <div className="sign-in--regular">
                        <div className="common-form-group">
                            {errorMessage && <p className="text-error">{errorMessage}</p>}

                            <div className="comp-input !mb-24">
                                <div className="input-field--modern">
                                    <input type="text" className="input--modern" inputMode={"email"}
                                           placeholder={t('@Email or Phone@')} name="qid" value={values.qid}
                                           onChange={onChange}/>
                                    <label className="input-label--modern">{t('@Email or Phone@')}</label>
                                </div>
                            </div>

                            <div className="comp-input">
                                <div className="input-field--modern">
                                    <div className="input-field--pwd">
                                        <span className={`input-toggle--pwd ic-eye-${visiblePasswd ? 'open' : 'close'}`}
                                              onClick={togglePassword}></span>
                                        <input type={visiblePasswd ? 'text' : 'password'} className="input--modern"
                                               placeholder={t('@Password@')} name="password" value={values.password}
                                               onChange={onChange}/>
                                        <label className="input-label--modern">{t('@Password@')}</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Captcha value={values.captcha} inputChangeHandler={onChange}
                                 resetValue={() => setValues(prev => ({...prev, captcha: ''}))}/>

                        <div className="common-form-group common-form-group--checkbox-group">
                            {getCurrentDomainRegion() === CHINA &&
                                <div className="comp-checkbox">
                                    <label className="q-checkbox">
                                        <input type="checkbox" checked={agreed} onChange={() => setAgreed(prev => !prev)} name="checkbox-terms"/>
                                        <span dangerouslySetInnerHTML={
                                            {
                                                __html: t('@I agree to the QNAP Cloud {{Terms of Service}} and {{Privacy Policy}}@', {
                                                        "Terms of Service": `<a class="pl-4 pr-4" href="${getQNAPUrl()}/${convertToQNAPLang(i18n.language)}/before_buy/con_show.php?op=showone&cid=15" target="_blank">${t('@Terms of service@')}</a>`,
                                                        "Privacy Policy": `<a class="pl-4 pr-4" href="${getPrivacyPolicyUrl()}" target="_blank">${t('@Privacy policy@')}</a>`
                                                    }
                                                )
                                            }
                                        }></span>
                                    </label>
                                </div>
                            }

                            <div className="comp-checkbox">
                                <label className="q-checkbox">
                                    <input type="checkbox" name="keep_me_signed_in" checked={values.keep_me_signed_in} onChange={(e)=> setValues(prev=> {return {...prev, keep_me_signed_in: !prev.keep_me_signed_in}})}/>
                                    <div className="display-inline-flex align-items-center">
                                        <span className="display-inline-block mr-8">
                                            {t('@Keep me signed in@')}
                                        </span>
                                        <img src={sys_info_svg} className="w-18px h-18px"
                                             data-tooltip-id="keep-me-signin"
                                             data-tooltip-content={t('@Extends session duration to 2 days. Not recommended for shared devices.@')}
                                             alt=""/>
                                        <Tooltip id="keep-me-signin"/>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <button className="q-btn q-btn--primary--focus btn-medium btn-rounded-normal"
                                data-btn-signin="regular"
                                type="submit"
                                disabled={!values.qid || !values.password || disableSignInBtn || (getCurrentDomainRegion() === CHINA && !agreed)}
                            // onClick={onLogin}
                        >{t('@Sign in@')}</button>

                        {
                            getCurrentDomainRegion() === CHINA &&
                            // <button className="q-btn q-btn--primary btn-medium btn-rounded-normal mt-16"
                            //         data-btn-signin="wechat" disabled={(getCurrentDomainRegion()===CHINA && !agreed)}>
                            //     <span className="ic-wechat"><img src={weixin_svg}/></span>
                            //     <span>使用微信账号登录</span>
                            // </button>
                            <>
                                {!urlHandler.isHideSocial() &&
                                    <SocialAccount region={getCurrentDomainRegion()} isAgreed={agreed}/>}
                            </>
                        }


                        {
                            !urlHandler.isHideSSO() && window.acc_mf.auth.ENABLE_SSO &&
                            <>
                                <div className="sign-in-divider mt-16 mb-16"></div>
                                <button className="q-btn q-btn--primary btn-medium btn-rounded-normal" data-btn-signin="sso" onClick={onSSOLogin}>
                                    {t('@Sign in with SSO@')}
                                </button>
                            </>
                        }
                    </div>
                }


                <div className="sign-in--options">
                    {
                        (!urlHandler.isHideSignUp() && !reachLoginLimit) &&
                        <p className="!mb-10">{t('@New User?@')}<a href={`/signup${window.location?.search ? window.location.search : ''}`}
                                                                   className="ml-5">{t('@Sign up now@')}</a></p>
                    }
                    {!reachLoginLimit && <p className="!mb-10"><a href={`/forget_password${location.search?.length > 0 ? location.search : ''}`}>{t('@Forgot Password?@')}</a></p>}
                    {
                        ((getCurrentDomainRegion() !== CHINA && !urlHandler.isHideSocial()) && !reachLoginLimit) && <p className="!mb-10"><a href="#" onClick={toggleSignOption}>{signInOption === 'social' ? t('@Sign in with my QID@') : t('@Sign in QID with social networks@')}</a></p>
                    }
                    {
                        reachLoginLimit && <p className="!mb-10"><a href={`/forget_password${location.search?.length > 0 ? location.search : ''}`}>{t('@Reset Password to Unlock Account@')}</a></p>
                    }
                    {
                        !urlHandler.isHideActivationMail() && <p><a href="/signup#send_activation_email">{t('@Have not received activation email?@')}</a></p>
                    }
                </div>
            </div>
            </form>
        </section>
    );
}

export default SigninComponent;